import { ChangeEventHandler, useState, useRef } from 'react';
import { ClassNames, DateRange, DayPicker, SelectRangeEventHandler } from 'react-day-picker';
import dayPickerStyles from 'react-day-picker/dist/style.module.css';
import { useOutsideClick } from '../../CustomHooks';
import { format, isAfter, isBefore, isValid, parse } from 'date-fns';
import styles from './DateRangePicker.module.scss';

interface Props {
  dateRange: DateRange;
  setDateRange: (arg0: DateRange) => void;
  startDate: Date;
  excludeBefore?: Date;
  excludeAfter?: Date;
  beforeFirstInputText?: string;
  beforeSecondInputText?: string;
  isDisplayCalendar?: boolean;
  setIsDisplayCalendar?: (arg0: boolean) => void;
  handleBeforeStart?: () => void;
  disabled?: boolean;
}

const DateRangePicker = ({
  dateRange,
  setDateRange,
  startDate,
  excludeAfter,
  excludeBefore,
  beforeFirstInputText,
  beforeSecondInputText,
  isDisplayCalendar,
  setIsDisplayCalendar,
  handleBeforeStart,
  disabled,
}: Props) => {
  const [fromValue, setFromValue] = useState<string>('');
  const [toValue, setToValue] = useState<string>('');
  const [isEndDateActive, setIsEndDateActive] = useState<boolean>(false);
  const calendarRef = useRef(null);

  useOutsideClick(calendarRef, () => setIsDisplayCalendar && setIsDisplayCalendar(false));

  const handleFromChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setFromValue(e.target.value);
    const date = parse(e.target.value, 'y-MM-dd', new Date());

    if (handleBeforeStart && date < startDate) handleBeforeStart();
    if (!isValid(date)) {
      return setDateRange({ from: undefined, to: dateRange.to });
    }

    if (dateRange?.to && isAfter(date, dateRange.to)) {
      setDateRange({ from: dateRange.to, to: date });
      setFromValue(format(dateRange.to, 'y-MM-dd'));
      setToValue(format(date, 'y-MM-dd'));
    } else {
      setDateRange({ from: date, to: dateRange?.to });
    }
  };

  const handleToChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setToValue(e.target.value);
    const date = parse(e.target.value, 'y-MM-dd', new Date());

    if (!isValid(date)) {
      return setDateRange({ from: dateRange?.from, to: undefined });
    }
    if (dateRange?.from && isBefore(date, dateRange.from)) {
      setDateRange({ from: date, to: dateRange.from });
    } else {
      setDateRange({ from: dateRange?.from, to: date });
    }
  };

  const handleRangeSelect: SelectRangeEventHandler = (range: DateRange | undefined) => {
    if (handleBeforeStart && range?.from && dateRange?.from && range?.from < startDate && range.from < dateRange?.from)
      handleBeforeStart();

    if (!isEndDateActive && dateRange?.to && range?.to && isBefore(range.to, dateRange.to)) {
      setFromValue(format(range?.to || startDate, 'y-MM-dd'));
      setToValue(format(dateRange?.to, 'y-MM-dd'));
      if (range) setDateRange({ from: range.to, to: dateRange.to });
      return;
    }

    if (range) setDateRange(range);
    if (range?.from) {
      setFromValue(format(range.from, 'y-MM-dd'));
    } else {
      setFromValue('');
    }
    if (range?.to) {
      setToValue(format(range.to, 'y-MM-dd'));
    } else {
      setToValue('');
    }
  };

  const handleStartDateFocus = () => {
    setIsEndDateActive(false);
    if (setIsDisplayCalendar) setIsDisplayCalendar(true);
  };

  const handleEndDateFocus = () => {
    setIsEndDateActive(true);
    if (setIsDisplayCalendar) setIsDisplayCalendar(true);
  };

  const dayPickerClassNames: ClassNames = {
    ...dayPickerStyles,
    head: styles.days,
    root: styles.dayPickerRoot,
    caption_label: styles.captionLabel,
    nav_button: styles.dayPickerNav,
    day_selected: styles.daySelected,
    cell: styles.cell,
    day: styles.day,
    day_range_end: styles.dayRangeEnd,
    day_range_start: styles.dayRangeStart,
    day_range_middle: styles.dayRangeMiddle,
    button: styles.dayPickerButton,
  };
  return (
    <div className={styles.container} ref={calendarRef}>
      <form onSubmit={(e) => e.preventDefault()}>
        {beforeFirstInputText && <h3>{beforeFirstInputText}</h3>}
        <input
          disabled={disabled}
          placeholder={startDate.toISOString().split('T')[0]}
          value={fromValue}
          onChange={handleFromChange}
          onFocus={handleStartDateFocus}
        />
        {beforeSecondInputText && <h3>{beforeSecondInputText}</h3>}
        <input
          disabled={disabled}
          placeholder={excludeAfter?.toISOString().split('T')[0] || 'YYYY-MM-DD'}
          value={toValue}
          onChange={handleToChange}
          onFocus={handleEndDateFocus}
        />
      </form>
      {isDisplayCalendar ? (
        <div className={styles.calendarContainer}>
          <DayPicker
            disabled={disabled}
            mode='range'
            classNames={dayPickerClassNames}
            defaultMonth={isEndDateActive ? dateRange?.to : dateRange?.from}
            numberOfMonths={2}
            selected={dateRange}
            onSelect={handleRangeSelect}
            fromDate={excludeBefore && excludeBefore}
            toDate={excludeAfter && excludeAfter}
          />
        </div>
      ) : null}
    </div>
  );
};

export default DateRangePicker;
